// #3155 -- scribe-visible notices for a sheet write score-api REFUSED (400 with
// a `problems` array) that sync.js then quarantined (#2812).
//
// Why this exists: the quarantine path removes the outbox op once the intake
// acks, so the scribe-facing save-error banner (useScoreSheet reads the op back
// after a flush) found nothing and cleared: a recorded deduction the server
// refused silently never landed. This is the one place that fact lives after
// the op is gone. Design decision (card #3155 AC1): the engine records a notice
// when it quarantines; a dismissible banner (QuarantineNotices.jsx, mounted in
// AppShell) shows it in scribe language, naming the refused deduction. It is
// NOT a dialog (a background drain must never steal focus mid-mark) and NOT an
// outbox row (the op is deliberately gone, #2812).
//
// Persisted to localStorage so a kiosk reload between the drain and the scribe
// looking up does not lose it (a dismissed notice is deleted; there is no
// escape hatch to keep it). Every storage access is try/catch'd: storage off
// degrades to in-memory only, never a throw.
const STORAGE_KEY = "scribe-fe.quarantineNotices";
const MAX_NOTICES = 20;

const REASONS = new Set(["notEnriched", "unknown", "mismatch", "cap"]);

/**
 * Narrow a 400 body's `data.problems` to well-formed entries. Anything else
 * (no array, wrong shape, an unrelated 400) yields [] so unrelated validation
 * 400s keep the plain quarantine behaviour with no notice.
 * @param {unknown} problems
 * @returns {Array<{ id: string, label: string, reason: string }>}
 */
export function readServerProblems(problems) {
  if (!Array.isArray(problems)) return [];
  return problems
    .filter(
      (p) =>
        p &&
        typeof p === "object" &&
        typeof p.label === "string" &&
        p.label.length > 0 &&
        typeof p.reason === "string",
    )
    .map((p) => ({
      id: typeof p.id === "string" ? p.id : "",
      label: p.label,
      // An unrecognised reason still gets a notice, in the generic copy.
      reason: REASONS.has(p.reason) ? p.reason : "unknown",
    }));
}

function load() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    // A malformed persisted entry must never reach render (AppShell mounts it).
    return parsed.filter(
      (n) =>
        n &&
        typeof n === "object" &&
        typeof n.noticeId === "string" &&
        n.noticeId.length > 0 &&
        Array.isArray(n.problems) &&
        n.problems.length > 0 &&
        n.problems.every(
          (p) => p && typeof p === "object" && typeof p.label === "string",
        ),
    );
  } catch {
    return [];
  }
}

let notices = load();
const listeners = new Set();

function commit(next) {
  notices = next;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notices));
  } catch {
    // Storage unavailable: in-memory only.
  }
  for (const fn of listeners) fn();
}

/** Stable snapshot for useSyncExternalStore (same array until a change). */
export function getQuarantineNotices() {
  return notices;
}

export function subscribeQuarantineNotices(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * noticeId is the sheet's entityKey when known, so a repeat refusal on the
 * same sheet replaces the earlier notice instead of stacking.
 * @param {{ noticeId: string, entityKey?: string|null, rideLabel?: string|null,
 *   problems: Array<{ id: string, label: string, reason: string }> }} notice
 */
export function addQuarantineNotice(notice) {
  if (!notice?.noticeId || !notice.problems?.length) return;
  const rest = notices.filter((n) => n.noticeId !== notice.noticeId);
  commit(
    [...rest, { ...notice, at: new Date().toISOString() }].slice(-MAX_NOTICES),
  );
}

export function dismissQuarantineNotice(noticeId) {
  if (!notices.some((n) => n.noticeId === noticeId)) return;
  commit(notices.filter((n) => n.noticeId !== noticeId));
}

// Test-only: reload module state from (cleared) storage.
export function _resetQuarantineNoticesForTests() {
  notices = load();
  for (const fn of listeners) fn();
}
